import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Hash } from "lucide-react";

export default function SlotsDialog({ open, onClose, authAxios, onRefresh }) {
  const [slotsDate, setSlotsDate] = useState(new Date().toISOString().split("T")[0]);
  const [slotsMax, setSlotsMax] = useState("");
  const [slotsInfo, setSlotsInfo] = useState(null);
  const [savingSlots, setSavingSlots] = useState(false);

  const loadSlotInfo = async (date) => {
    try {
      const res = await authAxios.get(`/reservations/slots?date=${date}`);
      setSlotsInfo(res.data);
      setSlotsMax(res.data.max_slots != null ? String(res.data.max_slots) : "");
    } catch {
      setSlotsInfo(null);
      setSlotsMax("");
    }
  };

  const handleDateChange = async (date) => {
    setSlotsDate(date);
    await loadSlotInfo(date);
  };

  const handleOpen = async () => {
    await loadSlotInfo(slotsDate);
  };

  const saveSlots = async () => {
    setSavingSlots(true);
    try {
      await authAxios.post("/reservations/slots", {
        date: slotsDate,
        max_slots: slotsMax === "" ? null : parseInt(slotsMax, 10),
      });
      toast.success(slotsMax === "" ? "Limite removido" : `Limite de ${slotsMax} reservas guardado`);
      await loadSlotInfo(slotsDate);
      onRefresh();
    } catch { toast.error("Erro ao guardar"); }
    setSavingSlots(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose} onOpenAutoFocus={handleOpen}>
      <DialogContent className="w-[95vw] max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Hash className="w-5 h-5 text-emerald-600" />Disponibilidade por Dia
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-xs">Data</Label>
            <Input type="date" value={slotsDate} onChange={e => handleDateChange(e.target.value)} />
          </div>

          {slotsInfo && (
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">Reservas confirmadas</span>
                <span className="font-semibold">{slotsInfo.booked}</span>
              </div>
              {slotsInfo.max_slots != null && (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Limite configurado</span>
                    <span className="font-semibold">{slotsInfo.max_slots}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Disponíveis</span>
                    <span className={`font-bold ${slotsInfo.available === 0 ? "text-red-600" : "text-emerald-600"}`}>
                      {slotsInfo.available}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-200 rounded-full mt-2">
                    <div
                      className={`h-2 rounded-full transition-all ${slotsInfo.available === 0 ? "bg-red-500" : "bg-emerald-500"}`}
                      style={{ width: `${Math.min(100, (slotsInfo.booked / slotsInfo.max_slots) * 100)}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          )}

          <div>
            <Label className="text-xs">Máximo de reservas para este dia</Label>
            <Input
              type="number" min="0" placeholder="Sem limite"
              value={slotsMax} onChange={e => setSlotsMax(e.target.value)}
              data-testid="slots-max-input"
            />
            <p className="text-[11px] text-gray-400 mt-1">Deixa vazio para remover o limite do dia.</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={saveSlots} disabled={savingSlots} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-slots-btn">
            {savingSlots ? "A guardar..." : "Guardar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
